<template>
    <!-- Delete confirmation for a usage record. Manual records keep the plain
         confirmation; an auto-tracked record (MY1-3081) owns up to who created
         it and offers the optional "What's wrong?" field — deliberately not
         labelled optional, per the design, to keep completion rates up. -->
    <UiModal
        :is-open="isOpen"
        title="Delete history record"
        icon="trash"
        icon-color="red"
        @close="$emit('close')"
    >
        <template #content>
            <div class="mt-2 space-y-4">
                <p
                    class="text-sm text-slate-800 dark:text-slate-100"
                    data-test="delete-copy"
                >
                    You're about to delete the
                    <template v-if="isAutoTracked && amountLabel">
                        {{ amountLabel }}
                    </template>
                    {{ benefitTitle }} usage recorded on {{ recordedOn }}. This
                    action cannot be undone.
                    <template v-if="isAutoTracked">
                        We tracked this usage automatically from My Spending.
                        Let us know if something's wrong.
                    </template>
                </p>

                <div v-if="isAutoTracked">
                    <label
                        :for="feedbackId"
                        class="block text-sm text-slate-800 dark:text-slate-100 mb-2"
                    >
                        What's wrong with the record?
                    </label>
                    <textarea
                        :id="feedbackId"
                        :value="feedback"
                        rows="3"
                        maxlength="1000"
                        placeholder="For example: the spending category is incorrect"
                        class="block w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:border-slate-800 dark:focus:border-slate-300 focus:ring-1 focus:ring-slate-800 dark:focus:ring-slate-300 focus:outline-none resize-none"
                        data-test="delete-feedback"
                        @input="$emit('update:feedback', $event.target.value)"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-end space-x-3">
                <button
                    type="button"
                    class="btn-base btn-white"
                    @click="$emit('close')"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn-base btn-transparent !bg-red-500 !text-white"
                    data-test="confirm-delete"
                    @click="$emit('confirm')"
                >
                    Delete
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { computed } from 'vue';
import dayjs from 'dayjs';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import filters from '@/filters';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    // The usage record being deleted (as serialized on the benefit payload).
    entry: {
        type: Object,
        default: null,
    },
    benefitTitle: {
        type: String,
        default: '',
    },
    // The "What's wrong?" answer (v-model:feedback).
    feedback: {
        type: String,
        default: '',
    },
    formatDate: {
        type: Function,
        default: (value) => (value ? dayjs(value).format('MMM D, YYYY') : ''),
    },
});

defineEmits(['close', 'confirm', 'update:feedback']);

const feedbackId = `delete-usage-feedback-${Math.random().toString(36).slice(2, 8)}`;

const isAutoTracked = computed(() => props.entry?.source === 'auto');

const recordedOn = computed(() => props.formatDate(props.entry?.used_at));

const amountLabel = computed(() => {
    const amount = parseFloat(props.entry?.total_amount_used ?? 0);

    return amount > 0 ? filters.formatSmartMoney(amount) : '';
});
</script>
